/**
 * useCapabilities — the caller's effective capability set, resolved
 * server-side by the permission engine (user overrides → role overrides →
 * catalog defaults).
 * Use `canDo('domain.action')` for UI hide/show; the API always re-checks.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context.js';

export function useCapabilities() {
  const { user, authSignal, refreshCapabilities } = useAuth();
  const query = useQuery({
    queryKey: ['my-permissions', user?.id, user?.role, authSignal],
    enabled: !!user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    queryFn: async ({ signal }) => {
      const verified = await refreshCapabilities();
      if (signal.aborted) { const error = new Error('Authentication identity changed'); error.name = 'AbortError'; throw error; }
      return verified;
    },
  });
  return {
    ...query,
    canDo: (capability: string) => query.data?.has(capability) ?? false,
  };
}
